/**
 * formatRelativeTime — italian relative-time helper used across the
 * workspace hubs (conversations / documents / calendar) to render row
 * timestamps like "15m", "2h", "ieri", "3g".
 *
 * v1 reference: each hub in v1 did this inline (`ChatPage.tsx` sidebar +
 * `EmailHubPage.tsx` row formatter). v2 consolidates here so every hub
 * row reads the same way.
 *
 * Pure function — no DOM / locale plumbing. Localised labels ("ora",
 * "ieri") resolve via the global ``@lingui/core`` singleton (same pattern
 * as ``lib/ingestionToast.ts``) so the helper stays signature-pure while
 * still feeding the active catalog. Numeric suffixes (m/h/g) and the
 * ``DD/MM`` fallback are locale-stable.
 */

import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";

/** Buckets are static to keep allocations zero per render. */
const MS_MIN = 60_000;
const MS_HOUR = 60 * MS_MIN;
const MS_DAY = 24 * MS_HOUR;

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const delta = Date.now() - t;
  if (delta < 0) return i18nSingleton._(msg`ora`);
  if (delta < MS_MIN) return i18nSingleton._(msg`ora`);
  if (delta < MS_HOUR) return `${Math.floor(delta / MS_MIN)}m`;
  if (delta < MS_DAY) return `${Math.floor(delta / MS_HOUR)}h`;
  if (delta < 2 * MS_DAY) return i18nSingleton._(msg`ieri`);
  if (delta < 7 * MS_DAY) return `${Math.floor(delta / MS_DAY)}g`;
  // Beyond 7 days — fall back to DD/MM (locale-stable, no Intl needed).
  const d = new Date(t);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
}
